import React from 'react';
import styled from '@emotion/styled';
import { Check, Circle, CheckCircle2 } from 'lucide-react';

/**
 * ChoiceGroup — a question with options to tick (multi) or to pick one of
 * (single). Pure: values in, onChange out. The same piece asks the Kysely's
 * claims and requirements, the expectation pick, and, after the workshop,
 * the change meter's re-ask; results are drawn by SpreadBar, not here.
 */
const Wrap = styled.fieldset`border: 0; margin: 0; padding: 0; min-width: 0; display: flex; flex-direction: column; gap: 0.5rem;`;
const Legend = styled.legend`padding: 0; margin: 0 0 0.35rem; font-weight: 600; color: #e8e8ef; line-height: 1.4;`;
const Hint = styled.p`margin: -0.1rem 0 0.35rem; font-size: 0.85rem; color: #7b8794;`;
const Option = styled.button`
  all: unset; box-sizing: border-box; cursor: pointer; width: 100%;
  display: flex; align-items: flex-start; gap: 0.8rem; padding: 0.8rem 1rem; border-radius: 10px;
  background: ${(p) => (p.$on ? 'rgba(91, 157, 217, 0.12)' : '#141821')};
  border: 1px solid ${(p) => (p.$on ? 'rgba(142, 193, 240, 0.55)' : '#232a36')};
  color: #e8e8ef; line-height: 1.45; text-align: left;
  &:hover, &:focus-visible { border-color: ${(p) => (p.$on ? '#8ec1f0' : '#3a4457')}; }
  &:disabled { cursor: default; opacity: 0.75; }
  .mark { flex: 0 0 auto; margin-top: 0.15rem; color: ${(p) => (p.$on ? '#8ec1f0' : '#4b5563')}; display: inline-flex; }
  .src { display: block; margin-top: 0.2rem; font-size: 0.78rem; color: #7b8794; }
`;

/**
 * @param {{ id: string, text: string, source?: string|null }[]} options
 * @param {string[]} values   selected ids (single: zero or one)
 * @param {'multi'|'single'} mode
 */
export default function ChoiceGroup({ question, hint = null, options = [], values = [], onChange, mode = 'multi', disabled = false, max = null }) {
  const selected = new Set(values);
  const toggle = (id) => {
    if (disabled || !onChange) return;
    if (mode === 'single') return onChange(selected.has(id) ? [] : [id]);
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else if (max && next.size >= max) return;
    else next.add(id);
    onChange([...next]);
  };
  return (
    <Wrap disabled={disabled} role={mode === 'single' ? 'radiogroup' : 'group'} aria-label={question}>
      {question && <Legend>{question}</Legend>}
      {hint && <Hint>{hint}</Hint>}
      {options.map((o) => {
        const on = selected.has(o.id);
        return (
          <Option key={o.id} type="button" $on={on} disabled={disabled} onClick={() => toggle(o.id)}
            role={mode === 'single' ? 'radio' : 'checkbox'} aria-checked={on}>
            <span className="mark">{mode === 'single' ? (on ? <CheckCircle2 size={18} /> : <Circle size={18} />) : (on ? <CheckCircle2 size={18} /> : <Check size={18} style={{ opacity: 0.35 }} />)}</span>
            <span>{o.text}{o.source && <span className="src">{o.source}</span>}</span>
          </Option>
        );
      })}
    </Wrap>
  );
}
